import { faGear, faSparkles } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useContext, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import type { SSE } from 'sse.js'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { startContactEmailDraft } from '~/components/Scopes/Actions/contactEmailDraft'
import { isHtmlEmpty, confirm as showConfirm, toast } from '~/utils'
import { AsideActionContext } from '~/utils/contextes'

type ContactEmailDraftButtonProps = {
  applicantId: number
  orderId: number
  disabled?: boolean
}

export const ContactEmailDraftButton = ({
  applicantId,
  orderId,
  disabled = false
}: ContactEmailDraftButtonProps) => {
  const { setValue, getValues } = useFormContext()
  const { setIsDirty } = useContext(AsideActionContext)
  const [isGenerating, setIsGenerating] = useState(false)
  const sseRef = useRef<SSE | null>(null)

  useEffect(() => {
    return () => {
      sseRef.current?.close()
    }
  }, [])

  const handleGenerate = async () => {
    if (
      !isHtmlEmpty(getValues('message')) &&
      !(await showConfirm(
        'Remplacer le contenu ?',
        'Un contenu est déjà présent dans le champ message. Il sera remplacé par le contenu généré.',
        'Remplacer'
      ))
    )
      return

    setIsGenerating(true)
    sseRef.current = startContactEmailDraft(applicantId, orderId, {
      onContent: (partial) =>
        setValue('message', partial, {
          shouldDirty: false,
          shouldValidate: false
        }),
      onDone: (subject, content) => {
        sseRef.current = null
        if (subject.trim()) {
          setValue('subject', subject, {
            shouldDirty: true,
            shouldValidate: true
          })
        }
        setValue('message', content, {
          shouldDirty: true,
          shouldValidate: true
        })
        setIsDirty(true)
        setIsGenerating(false)
      },
      onError: () => {
        sseRef.current = null
        setIsGenerating(false)
        toast(
          'Erreur de génération',
          "Une erreur est survenue lors de la génération du contenu de l'email.",
          ToastType.Danger
        )
      }
    })
  }

  return (
    <Button
      type="button"
      variation={ButtonVariation.AI}
      buttonSize="small"
      disabled={isGenerating || disabled}
      onClick={() => void handleGenerate()}
    >
      <FontAwesomeIcon icon={isGenerating ? faGear : faSparkles} />
      {isGenerating ? 'Génération...' : 'Générer'}
    </Button>
  )
}
